import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHotkeys } from "react-hotkeys-hook";
import { connect, ConnectedProps } from "react-redux";

import {
  $Dropdown,
  $Tooltip,
  $Badge,
  $Button,
  $Skeleton,
  $Empty,
  $Notification,
} from "us.common/components";
import { BoxIcons, IconTypes, MessageOutlined } from "us.icons";
import { notification } from "us.common/actions";
import { INotificationMenu, IManageNotification } from "./Interface";
import * as Azure from "us.helper/azure";
import {
  NotificationUpdateType,
  NotificationTriggerType,
} from "us.common/constants/Component/Notification";
import { openGlobalNotification } from "us.common/components/NotificationMenu/Functions";
import { UpdateNotification } from "us.common/components/NotificationMenu/Repository";
import { useSignalRTriggers } from "us.common/SignalRProvider";
import { NotificationItem } from "./Components";
import { HotKeys } from "us.common/constants";
import { useDrawer } from "us.common/context";
import { ArgsProps } from "antd/lib/notification";
import { isJsonString } from "us.common/functions";

/**
 * @description - Notification menu component for view available notification and manage them.
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 23/05/2022
 * */
const NotificationMenu: React.FC<INotificationMenu & PropsFromRedux> = ({
  notifications,
  pendingNotifications,
  notificationUnReadCount,
  notificationUpdateId,
  isNotificationsFetching,
  updatePendingNotification,
  getNotifications,
  updateNotification,
}) => {
  const { t } = useTranslation();
  const { isAnyDrawerOpen } = useDrawer();

  const [manageNotification, setManageNotification] =
    useState<IManageNotification>({
      visible: false,
      offset: 5,
    });

  const authentication: Azure.ADAuth = new Azure.ADAuth();
  const currentUser: any = authentication.currentUser();

  const triggers = useMemo(
    () => [
      {
        name: NotificationTriggerType.ON_NOTIFICATION_ADDED,
        callBack: () => {
          getNotifications &&
            getNotifications({
              id: currentUser.oid,
              offset: 0,
            });
        },
      },
      {
        name: NotificationTriggerType.ON_FOLLOWUP_REIMPORT,
        callBack: () => {
          getNotifications &&
            getNotifications({
              id: currentUser.oid,
              offset: 0,
            });
        },
      },
      {
        name: NotificationTriggerType.ON_EVENT_ACK,
        callBack: (content: any) => {
          const { eventID, isInProgress } = content ?? {};
          openGlobalNotification(content);
          updatePendingNotification &&
            updatePendingNotification({
              eventId: eventID,
              isInProgress,
            });
        },
      },
      {
        name: NotificationTriggerType.ON_EVENT_DONE,
        callBack: (content: any) => {
          const { eventID, isInProgress } = content ?? {};
          openGlobalNotification(content);
          updatePendingNotification &&
            updatePendingNotification({
              eventId: eventID,
              isInProgress,
            });
        },
      },
      {
        name: NotificationTriggerType.ON_RECEIVE_CREDITOR_PORTAL_MESSAGE,
        callBack: (content: any) => {
          const { eventID } = content ?? {};
          let message: string = "",
            caseHandlerText: string = "",
            title: string = "NEW_MESSAGE_RECEIVED";
          if (isJsonString(content)) {
            const parsedContent = JSON.parse(content);
            title = t(`US.COMMON:NOTIFICATIONS.${parsedContent.title}`);
            message = t(`US.COMMON:NOTIFICATIONS.${parsedContent.message}`, {
              caseNo: parsedContent?.data?.caseNo || "",
              caseHandler: "",
            });
            caseHandlerText = parsedContent?.data?.caseHandler
              ? t(`US.COMMON:NOTIFICATIONS.CASE_HANDLER`, {
                  caseHandler: parsedContent?.data?.caseHandler || "",
                })
              : "";
          }
          const notificationProps: ArgsProps = {
            message: <strong>{title}</strong>,
            description: (
              <div className="mt-1">
                <div>{message}</div>
                {caseHandlerText && (
                  <div className="ant-form-item-extra">{caseHandlerText}</div>
                )}
              </div>
            ),
            key: eventID,
            placement: "topRight",
            top: 54,
            icon: <MessageOutlined style={{ color: "#1890ff" }} />,
            className: "cp-message-notification",
            duration: 7.5,
          };
          $Notification.info(notificationProps);
          getNotifications &&
            getNotifications({
              id: currentUser.oid,
              offset: 0,
            });
        },
      },
    ],
    [currentUser.oid, getNotifications],
  );

  useSignalRTriggers(triggers);

  useEffect(() => {
    getNotifications && getNotifications({ id: currentUser.oid, offset: 0 });
  }, []);

  useHotkeys(
    HotKeys.NOTIFICATION_MENU,
    () => {
      if (!manageNotification.visible) {
        getNotifications &&
          getNotifications({ id: currentUser?.oid, offset: 0 });
      }
      setManageNotification({
        ...manageNotification,
        visible: !manageNotification.visible,
      });
    },
    { enabled: !isAnyDrawerOpen },
  );

  /**
   * @function
   * @description - onRemoveNotification is a function that remove selected notification.
   * @param {number} notificationId - the id of the notification
   */
  const onRemoveNotification = (notificationId: number) => {
    const updateParams = UpdateNotification.call({
      notificationId: notificationId,
      userId: currentUser.oid,
      type: NotificationUpdateType.DELETE,
    });
    updateNotification && updateNotification(updateParams);
  };

  /**
   * @description - Menu content for notification.
   */
  const notificationMenu = {
    items: [
      {
        key: "title",
        label: (
          <div className="d-flex mb-2 justify-content-between notification-item">
            <span className="font-weight-bold">
              {t("US.COMMON:COMMON.NOTIFICATION")}
            </span>
            {notifications?.length > 0 && (
              <$Button
                type="link"
                size="small"
                className="font-sm mt-2 mr-n2"
                loading={notificationUpdateId === -1}
                onClick={() => onRemoveNotification(-1)}
              >
                {t("US.COMMON:COMMON.CLEAR_ALL")}
              </$Button>
            )}
          </div>
        ),
      },
      ...(notifications?.map((notification: any) => ({
        key: notification.notificationId,
        label: (
          <NotificationItem
            data={notification}
            onRemoveNotification={onRemoveNotification}
          />
        ),
        onClick: () => {
          const updateParams = UpdateNotification.call({
            notificationId: notification.notificationId,
            userId: currentUser.oid,
          });
          updateNotification && updateNotification(updateParams);
          setManageNotification({
            ...manageNotification,
            visible: true,
          });
        },
      })) || []),
      isNotificationsFetching && {
        key: "skeleton",
        label: (
          <$Skeleton
            paragraph={{ rows: 1 }}
            loading={true}
            active
            className="m-3"
          />
        ),
      },
      notifications?.length === 0 &&
        !isNotificationsFetching && {
          key: "empty",
          label: (
            <$Empty
              image={$Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("US.COMMON:COMMON.NO_NOTICES_RIGHT_NOW")}
            />
          ),
        },
      ...(notifications?.length > 0
        ? [
            {
              key: "divider",
              type: "divider",
            },
            {
              key: "viewMore",
              label: (
                <div className="mb-1 text-center notification-item">
                  <a
                    onClick={() => {
                      getNotifications &&
                        getNotifications({
                          id: currentUser.oid,
                          offset: manageNotification.offset,
                          scroll: true,
                        });
                      setManageNotification({
                        ...manageNotification,
                        offset: manageNotification.offset + 5,
                      });
                    }}
                  >
                    {t("US.COMMON:COMMON.VIEW_MORE")}
                  </a>
                </div>
              ),
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <$Dropdown
        menu={notificationMenu}
        className="d-flex flex-row"
        trigger={["click"]}
        overlayClassName="notfication-dropdown"
        placement="bottomLeft"
        arrow
        open={manageNotification.visible}
        onOpenChange={(open: boolean) => {
          if (open) {
            getNotifications &&
              getNotifications({
                id: currentUser.oid,
                offset: 0,
              });
            setManageNotification({
              offset: 5,
              visible: open,
            });
          } else {
            setManageNotification({
              ...manageNotification,
              visible: open,
            });
          }
        }}
      >
        <$Tooltip
          placement="bottomLeft"
          title={t("US.COMMON:COMMON.NOTIFICATION")}
        >
          <a
            className={`d-flex align-items-center pt-1 ant-dropdown-link px-3 ${
              pendingNotifications.length > 0 ? "noti-progress" : ""
            }`}
          >
            <$Badge count={notificationUnReadCount}>
              <BoxIcons
                type={IconTypes.BOX_ICON}
                name="bell"
                className="text-white"
              />
            </$Badge>
          </a>
        </$Tooltip>
      </$Dropdown>
    </>
  );
};

const mapStateToProps = (state: any) => {
  const { notification } = state;
  const { unReadCount, pendingEvents, data, notificationUpdateId, isLoading } =
    notification;
  return {
    notifications: data,
    pendingNotifications: pendingEvents,
    notificationUnReadCount: unReadCount,
    isNotificationsFetching: isLoading,
    notificationUpdateId,
  };
};

const { user, event, update } = notification;
const mapDispatchToProps = {
  getNotifications: user.get,
  updateNotification: update.save,
  updatePendingNotification: event.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(NotificationMenu);
